// Shape of `Job.output` for `HARMONIZE_VIOLATIONS` jobs.
//
// Single source of truth for the ECS task that produces the stats and the Oak
// frontend that renders them. Both import from here so the shape can't drift.

export type HarmonizeAirtableStats =
  | {
      error: string;
      /** Present when batches committed before the failure; used for partial-success reporting. */
      uploaded?: number;
    }
  | {
      /**
       * The stage did not run at all — today, because the environment has no
       * Airtable key. Its own shape rather than a zeroed upload, so a run that
       * never attempted the sync cannot be misread as one that had nothing to
       * upload, and so skipping does not have to borrow the error shape and drag
       * the whole refresh to FAILED.
       */
      skipped: true;
      reason: string;
    }
  | {
      uploaded: number;
      skippedExisting: number;
      facilitiesCreated: string[];
      duplicateIdsInBatch: string[];
    };

/**
 * Stats object stored in `Job.output` (and `HarmonizedViolationRefresh.stats`)
 * for a HARMONIZE_VIOLATIONS run when at least one tenant is flag-enabled.
 * For the no-tenants early-return path, see `HarmonizeNoTenantsStats`.
 */
export type HarmonizeRunStats = {
  sourceCounts: Record<string, number>;
  facilityMatchCount: number;
  /**
   * Facilities with any FRS/RCRAINFO/EPA alternate id, expired or not — the
   * population the zero-check uses when expired-id matching is enabled.
   * Optional for the same historical-rows reason as `orderMatchCount`: the
   * type guard rejects payloads missing a required key, which would blank the
   * staff Jobs dialog for every run persisted before this field existed.
   */
  widenedFacilityMatchCount?: number;
  programCounts: Record<string, number>;
  netNewCount: number;
  statusFlipCount: number;
  staleCount: number;
  /**
   * Rows across all three programs whose determinant indicates an active
   * formal EPA Order (`stdSeverityDeterminantCode === 'ORDER'`).
   *
   * A run-total of 0 is the ordinary steady state, not a signal that the rule
   * broke: an Order escalates only its own program AND settled orders do not
   * escalate at all. `run.ts` treats 0 as suspicious only when it is a CHANGE
   * against a prior eligible run (see `orderEscalationSuppressed`).
   *
   * Optional: rows persisted before this field existed lack it. Kept
   * optional (rather than required) so `isHarmonizeRunStats` still accepts
   * those historical rows and the staff Jobs dialog keeps rendering them
   * instead of going blank.
   */
  orderMatchCount?: number;
  /**
   * Rows whose determinant is a state-issued formal enforcement action
   * (`stdSeverityDeterminantCode === 'STATE_ORDER'`, waste-only today).
   * Optional for the same historical-rows reason as `orderMatchCount`.
   */
  stateOrderMatchCount?: number;
  /** STAGED rows pre-dedup carrying a non-null `priorOccurrenceCount`, so it can
   * exceed a SQL count of that column — see the arch doc's run-stats rundown. */
  repeatMatchCount?: number;
  /**
   * STAGED rows (pre-dedup) matched only through a shared FRS registry
   * (`matchProvenance === 'SHARED_FRS'`) — the prior-owner fan-out. A
   * tenant's first flag-on run spikes `netNewCount` by design as the
   * registry's cross-program history backfills; this counter is what makes
   * that spike legible. Optional for the same historical-rows reason as
   * `orderMatchCount`.
   */
  sharedFrsMatchCount?: number;
  /**
   * Rows the dead-permit gate admitted (`permitTerminalDate` set), across
   * WATER and WASTE. Counted on the date rather than the determinant so a
   * firing that an Order outranks in the code column still counts — this
   * stat exists to answer "did the dead-permit rule ever fire" for a rule
   * expected to fire rarely. Optional for the same historical-rows reason
   * as `orderMatchCount`.
   */
  deadPermitMatchCount?: number;
  /**
   * Human-readable ADVISORY guardrail breaches from Stage 0 (see
   * `stages/guardrails.ts`'s `TABLE_SEVERITY`) — e.g. an enrichment source
   * table (CaseEnforcements, CaseFacilities, NpdesPermit) came up empty.
   * These never fail the run; they are carried here purely so a past run
   * can be investigated. Optional: rows persisted before this field existed
   * lack it, and a run with zero advisory breaches may omit it too.
   */
  warnings?: string[];
  /**
   * Count of rows whose stored determinant is `PERMIT_EXPIRED` and whose
   * severity was protected from a downgrade this run because Stage 0 raised
   * `severityFreeze.permit` (NpdesPermit unhealthy). Populated only when that
   * flag was set; absent on healthy runs and on rows persisted before this
   * field existed.
   */
  frozenPermitCount?: number;
  /**
   * Count of rows whose stored determinant is `ORDER` and whose severity was
   * protected from a downgrade this run because Stage 0 raised
   * `severityFreeze.order` (the ICIS-FEC case tables unhealthy). Populated
   * only when that flag was set; absent on healthy runs and on rows
   * persisted before this field existed.
   */
  frozenOrderCount?: number;
  /**
   * True when a table the Order rule's closure exclusions depend on was
   * unhealthy, so this run applied NO new Order escalations. Persisted, not just
   * logged: the count is 0 by construction, so a suppressed run must not anchor a
   * later run's zero-match comparison. Absent means not suppressed.
   */
  orderEscalationSuppressed?: boolean;
  /** Absent on runs persisted before Field Notes existed. */
  fieldNotes?: FieldNotesQueueOutcome;
  airtable: HarmonizeAirtableStats;
};

export const FIELD_NOTES_QUEUE_OUTCOMES = [
  "queued",
  "up-to-date",
  "not-enabled",
  "failed",
] as const;
export type FieldNotesQueueOutcome = (typeof FIELD_NOTES_QUEUE_OUTCOMES)[number];

function isFieldNotesQueueOutcome(value: unknown): value is FieldNotesQueueOutcome {
  return FIELD_NOTES_QUEUE_OUTCOMES.some((outcome) => outcome === value);
}

/** Minimal object returned when there are no enabled tenants. */
export type HarmonizeNoTenantsStats = {
  note: string;
};

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function isStringArray(v: unknown): v is string[] {
  return Array.isArray(v) && v.every((x) => typeof x === "string");
}

function isStringNumberRecord(v: unknown): v is Record<string, number> {
  if (!isPlainObject(v)) return false;
  return Object.values(v).every((val) => typeof val === "number");
}

function isHarmonizeAirtableStats(v: unknown): v is HarmonizeAirtableStats {
  if (!isPlainObject(v)) return false;
  // Error shape
  if ("error" in v) {
    return typeof v.error === "string";
  }
  // Skipped shape — a reason is required, because a skip with no explanation is
  // indistinguishable from a bug that swallowed the stage.
  if ("skipped" in v) {
    return v.skipped === true && typeof v.reason === "string";
  }
  // Success shape
  if (typeof v.uploaded !== "number") return false;
  if (typeof v.skippedExisting !== "number") return false;
  if (!isStringArray(v.facilitiesCreated)) return false;
  if (!isStringArray(v.duplicateIdsInBatch)) return false;
  return true;
}

/**
 * Narrow a loosely-typed value into a `HarmonizeNoTenantsStats`.
 * Returns `true` when the object has exactly the `note` string field, which is
 * what `runHarmonizeViolations` returns on the no-enabled-tenants early path.
 */
export function isHarmonizeNoTenantsStats(value: unknown): value is HarmonizeNoTenantsStats {
  if (!isPlainObject(value)) return false;
  return typeof value.note === "string";
}

/** Every optional numeric counter; the `keyof` bound is what stops this list
 * naming a field that does not exist. */
const OPTIONAL_NUMERIC_FIELDS = [
  "orderMatchCount",
  "stateOrderMatchCount",
  "repeatMatchCount",
  "sharedFrsMatchCount",
  "deadPermitMatchCount",
  "widenedFacilityMatchCount",
  "frozenPermitCount",
  "frozenOrderCount",
] as const satisfies readonly (keyof HarmonizeRunStats)[];

/**
 * Narrow a loosely-typed value (e.g. `Job.output` from the GraphQL `JSON`
 * scalar) into a `HarmonizeRunStats` without an `as` cast. Returns `true`
 * only when every required field is present with the correct type. An
 * upstream change that drops a field will fail this check up-front rather than
 * silently render empty cells in the staff-facing dialog.
 */
export function isHarmonizeRunStats(value: unknown): value is HarmonizeRunStats {
  if (!isPlainObject(value)) return false;
  if (!isStringNumberRecord(value.sourceCounts)) return false;
  if (typeof value.facilityMatchCount !== "number") return false;
  if (!isStringNumberRecord(value.programCounts)) return false;
  if (typeof value.netNewCount !== "number") return false;
  if (typeof value.statusFlipCount !== "number") return false;
  if (typeof value.staleCount !== "number") return false;
  for (const field of OPTIONAL_NUMERIC_FIELDS) {
    if (field in value && typeof value[field] !== "number") return false;
  }
  // Optional: absent on historical rows, and on runs with no advisory breaches.
  if ("warnings" in value && !isStringArray(value.warnings)) return false;
  // Optional: absent on historical rows and on runs that were not suppressed.
  if ("orderEscalationSuppressed" in value && typeof value.orderEscalationSuppressed !== "boolean")
    return false;
  if ("fieldNotes" in value && !isFieldNotesQueueOutcome(value.fieldNotes)) return false;
  if (!isHarmonizeAirtableStats(value.airtable)) return false;
  return true;
}
